import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { report } from '@/lib/telemetry'
import {
  loadLensChoice,
  pickDefaultCamera,
  rearCameras,
  resolveChoice,
  saveLensChoice,
  toCameraOptions,
  toChoice,
  type CameraOption,
} from './lens'

export type CameraStatus = 'idle' | 'starting' | 'live' | 'denied' | 'error'

interface CameraContextValue {
  status: CameraStatus
  stream: MediaStream | null
  error: string | null
  cameras: CameraOption[]
  activeCamera: CameraOption | null
  /** Idempotent: returns the existing stream if one is already live. */
  start: () => Promise<MediaStream | null>
  switchCamera: (camera: CameraOption) => Promise<void>
  /** True only where the platform genuinely supports it — never on iOS. */
  torchSupported: boolean
  torchOn: boolean
  setTorch: (on: boolean) => Promise<void>
}

const CameraContext = createContext<CameraContextValue | null>(null)

/**
 * Owns the camera for the whole app session.
 *
 * This provider is mounted *above* the router, and that placement is the entire
 * fix for "every time the camera starts, you're prompted for permission". The
 * old app mounted its scanner per visit and called `stop()` on unmount, so every
 * single scan was a fresh `getUserMedia` and therefore a fresh prompt.
 *
 * Here the track is acquired once and kept. Leaving the scanner pauses the
 * decode loop and detaches the preview; it never stops the track.
 *
 * What this cannot fix: iOS does not persist camera permission for standalone
 * Home Screen web apps at all, so there is still one prompt per app launch.
 * That is a WebKit limitation, not something a different implementation avoids.
 */
export function CameraProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<CameraStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [cameras, setCameras] = useState<CameraOption[]>([])
  const [activeCamera, setActiveCamera] = useState<CameraOption | null>(null)
  const [torchOn, setTorchOn] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)

  const streamRef = useRef<MediaStream | null>(null)
  const startingRef = useRef<Promise<MediaStream | null> | null>(null)
  const [, forceRender] = useState(0)

  const stopStream = useCallback(() => {
    const stream = streamRef.current
    if (stream === null) return
    for (const track of stream.getTracks()) track.stop()
    streamRef.current = null
    setStatus('idle')
    setTorchOn(false)
    forceRender((n) => n + 1)
  }, [])

  const applyTrackCapabilities = useCallback((stream: MediaStream) => {
    const track = stream.getVideoTracks()[0]
    if (track === undefined) return

    // `getCapabilities` is itself absent on some WebKit versions.
    const caps =
      typeof track.getCapabilities === 'function'
        ? (track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean })
        : undefined

    // Torch is simply not exposed on iOS — any browser, since they all use
    // WebKit. Feature-detect rather than shipping a button that silently fails.
    setTorchSupported(caps?.torch === true)
  }, [])

  const openStream = useCallback(
    async (camera: CameraOption | null): Promise<MediaStream> => {
      const video: MediaTrackConstraints = {
        // Capture high, decode a downscaled crop. Resolution is what lets a
        // small code resolve from a natural distance instead of being held
        // right up against it.
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30 },
      }

      if (camera !== null && camera.deviceId !== '') {
        // Explicit deviceId, not facingMode: on iOS >= 16.4 `environment`
        // frequently binds the ultra-wide, which cannot hold focus at normal
        // scanning distance.
        video.deviceId = { exact: camera.deviceId }
      } else {
        video.facingMode = { ideal: 'environment' }
      }

      return navigator.mediaDevices.getUserMedia({ video, audio: false })
    },
    [],
  )

  const refreshCameras = useCallback(async (): Promise<CameraOption[]> => {
    try {
      // Labels are blank until permission has been granted at least once, so
      // this is only meaningful after the first successful getUserMedia.
      const devices = await navigator.mediaDevices.enumerateDevices()
      const options = toCameraOptions(devices)
      setCameras(options)
      return options
    } catch (err) {
      report({
        level: 'warn',
        event: 'camera.enumerate_failed',
        message: err instanceof Error ? err.message : String(err),
      })
      return []
    }
  }, [])

  const start = useCallback(async (): Promise<MediaStream | null> => {
    if (streamRef.current !== null) return streamRef.current
    // Concurrent callers (scanner mounting while a retry is in flight) must not
    // trigger two getUserMedia calls — that is two permission prompts.
    if (startingRef.current !== null) return startingRef.current

    const run = async (): Promise<MediaStream | null> => {
      setStatus('starting')
      setError(null)

      try {
        // First open with whatever we can, to unlock device labels.
        const initial = await openStream(null)
        const options = await refreshCameras()

        const wanted = resolveChoice(rearCameras(options), loadLensChoice())

        let stream = initial
        const currentId = initial.getVideoTracks()[0]?.getSettings().deviceId

        // Reopen only if the browser gave us a different lens than the one the
        // user chose. Reopening is cheap here — permission is already granted,
        // so it does not prompt again.
        if (wanted !== null && wanted.deviceId !== '' && wanted.deviceId !== currentId) {
          for (const track of initial.getTracks()) track.stop()
          stream = await openStream(wanted)
        }

        streamRef.current = stream
        setActiveCamera(
          wanted ?? pickDefaultCamera(options) ?? null,
        )
        applyTrackCapabilities(stream)
        setStatus('live')

        // A track can end on its own: another app takes the camera, or iOS
        // reclaims it after a long background. Without this the preview goes
        // black and never recovers.
        const track = stream.getVideoTracks()[0]
        if (track !== undefined) {
          track.addEventListener('ended', () => {
            report({ level: 'warn', event: 'camera.track_ended' })
            streamRef.current = null
            setStatus('idle')
          })
        }

        return stream
      } catch (err) {
        const name = err instanceof DOMException ? err.name : ''
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setStatus('denied')
          setError('Camera access was blocked.')
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          setStatus('error')
          setError('No usable camera was found on this device.')
        } else {
          setStatus('error')
          setError(err instanceof Error ? err.message : 'The camera could not be started.')
        }
        report({
          level: 'error',
          event: 'camera.acquire_failed',
          message: err instanceof Error ? err.message : String(err),
          context: { name },
        })
        return null
      }
    }

    // Publish the guard *before* anything can clear it, then clear it only once
    // this exact attempt settles.
    //
    // The obvious shape — clearing the guard in a `finally` inside the async
    // IIFE — has a real failure mode: if the body throws before its first
    // `await` (which happens when `navigator.mediaDevices` is undefined, i.e.
    // any non-secure context), the finally runs synchronously and nulls the ref
    // *before* the assignment below sets it. The guard would then hold a
    // permanently settled promise, and every later call — including the "Try
    // again" button — would return that stale rejection instead of retrying.
    const attempt = run()
    startingRef.current = attempt
    void attempt.finally(() => {
      if (startingRef.current === attempt) startingRef.current = null
    })
    return attempt
  }, [applyTrackCapabilities, openStream, refreshCameras])

  const switchCamera = useCallback(
    async (camera: CameraOption) => {
      saveLensChoice(toChoice(camera))
      setActiveCamera(camera)

      // Switching lens genuinely requires a new track. Permission is already
      // granted at this point, so it does not re-prompt.
      const previous = streamRef.current
      try {
        const next = await openStream(camera)
        if (previous !== null) for (const track of previous.getTracks()) track.stop()
        streamRef.current = next
        applyTrackCapabilities(next)
        setStatus('live')
        forceRender((n) => n + 1)
      } catch (err) {
        report({
          level: 'error',
          event: 'camera.switch_failed',
          message: err instanceof Error ? err.message : String(err),
        })
        setError('That lens could not be opened. Reverting.')
      }
    },
    [applyTrackCapabilities, openStream],
  )

  const setTorch = useCallback(async (on: boolean) => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (track === undefined) return
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] })
      setTorchOn(on)
    } catch (err) {
      setTorchSupported(false)
      report({
        level: 'warn',
        event: 'camera.torch_failed',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }, [])

  useEffect(() => {
    // Release the camera when the app is actually going away. `pagehide` is the
    // reliable signal on iOS; `beforeunload` does not fire for standalone web
    // apps. Not doing this leaves the green camera indicator on after the user
    // has switched away, which reads as the app spying on them.
    const onPageHide = () => stopStream()
    window.addEventListener('pagehide', onPageHide)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      stopStream()
    }
  }, [stopStream])

  const value = useMemo<CameraContextValue>(
    () => ({
      status,
      stream: streamRef.current,
      error,
      cameras: rearCameras(cameras),
      activeCamera,
      start,
      switchCamera,
      torchSupported,
      torchOn,
      setTorch,
    }),
    [status, error, cameras, activeCamera, start, switchCamera, torchSupported, torchOn, setTorch],
  )

  return <CameraContext.Provider value={value}>{children}</CameraContext.Provider>
}

export function useCamera(): CameraContextValue {
  const ctx = useContext(CameraContext)
  if (ctx === null) throw new Error('useCamera must be used inside <CameraProvider>')
  return ctx
}
